import { produce } from "immer";
import ContentsService, {
  AI_POWERED_FACET,
  DeviceType,
} from "src/services/ContentsService";
import { useRouter } from "next/router";
import { create } from "zustand";
import { useMemo } from "react";
import { BhapticsContent } from "@/store/reducers/contents";
import { FilterType } from "@/v2-components/pages/games/_type";
import { usePathname } from "next/navigation";
import { ParsedUrlQuery } from "querystring";
import {
  useGameOptions,
  useGames,
  DEFAULT_GAME_OPTIONS,
  GameQueryOptions,
  VR_PLATFORM_FILTERS,
} from "@/api/game-api";
import { PLATFORM_FILTER_OPTIONS } from "@/constants/game-filter-options";
import { useQuery } from "@tanstack/react-query";
import { useProducts } from "@/services/product/useProductService";
import { useLocale } from "@/store/locale-store";
import {
  UNKNOWN_KEY_COUNTS_REFETCH_MS,
  useGameResellsKeyCounts,
} from "@/hooks/useGameResell";
import { useCurrency } from "@/hooks/useCurrency";
import {
  platformKeyStock,
  sellableKeyPlatforms,
} from "@/libs/cart/cart-game-stock";
import { isBundleOnlyGameResell } from "@/libs/games/bundle-only-game-key";

type Store = {
  text: string;
  setText: (s: string) => void;
};

const useStore = create<Store>()((set) => ({
  text: "",
  setText: (text: string) => {
    set((state) => ({ text }));
  },
}));

/**
 * **경로가 스스로 정하는 facet** — 사용자가 고른 게 아니다 (#5434).
 *
 * `useFilters` 에서 떼어냈다. 필터를 적용할 때 이 값을 **쿼리에 쓰면 안 되기** 때문이다:
 * 쿼리에 쓰면 다음 렌더에서 `useFilters` 가 「쿼리 + 유도」로 다시 합쳐 **같은 facet 이
 * 매번 하나씩 늘어난다**(`?filter=steam,native` → `steam,native,steam` → …).
 *
 * 지금까지 안 드러난 이유는 필터 적용이 **`/games/search` 로 튕겼기** 때문이다 — 그
 * 경로는 유도 facet 이 없어서 중복될 자리가 없었다. 경로를 유지하도록 고치면서 같이
 * 잡아야 하는 짝이다.
 */
export const usePathFacets = (): string[] => {
  const pathname = usePathname();
  if (pathname.includes("meta-quest")) return ["quest"];
  if (pathname.includes("steam-vr")) return ["steam"];
  if (pathname.includes("ps-vr2")) return ["psvr2"];
  if (pathname.includes("sim-racing")) return ["simracing"];
  if (pathname.includes("pico")) return ["pico"];
  // `/games/vr` 은 단일 플랫폼이 아니라 VR facet 전체다 (#5350).
  //
  // 매칭을 `"vr"` 이 아니라 **`"games/vr"`** 로 한다 — `"vr"` 이면 `/games/steam-vr`
  // 과 `/games/ps-vr2` 도 걸려서 위 분기 순서에 의존하게 된다. 경로 조각으로 맞추면
  // 순서와 무관하게 `/games/vr` 에만 붙는다(`/kr/games/vr` 같은 locale 접두사도 포함).
  if (pathname.includes("games/vr")) return [...VR_PLATFORM_FILTERS];
  if (pathname.includes("pc")) return ["pc"];
  return [];
};

/** 사용자가 **고를 수 있는** 플랫폼 값 — 유도 facet 과 같은 어휘다. */
const PLATFORM_VALUES = PLATFORM_FILTER_OPTIONS.map((o) => o.value);

export const useFilters = () => {
  const router = useRouter();
  const filter = router.query["filter"];
  const filters = filter?.toString().split(",") ?? ([] as string[]);
  const pathFacets = usePathFacets();

  // ★**경로 유도 facet 은 「기본값」이지 「강제 제약」이 아니다** (#5434 R1 Major).
  //
  // 쿼리에 **플랫폼 그룹 값이 하나라도 있으면** 사용자가 그 그룹을 직접 만졌다는 뜻이라
  // 유도분을 얹지 않는다. 안 그러면 `/games/vr`(유도 6종)에서 「Quest 만 보기」가
  // 아예 안 되고, 필터 UI 는 떠 있는데 눌러도 아무 일이 없는 **죽은 컨트롤**이 된다.
  //
  // 종전(base)에는 이 문제가 안 보였다 — 필터를 만지면 `/games/search` 로 **튕겨서**
  // 유도 facet 을 벗어났기 때문이다. 경로를 유지하도록 고치면 그 출구가 막히므로
  // 대체 수단이 필요하다.
  //
  // ★**규칙은 「플랫폼 집합이 비면 화면 기본값, 비지 않으면 사용자 것」이다.**
  // 「그 화면의 플랫폼으로 제약한다」가 **아니다** — 헷갈리기 쉬워 명시한다:
  //
  //   /games/meta-quest + filter=pc  →  ["pc"]     Quest 화면에 PC 게임이 나온다
  //   /games/vr         + filter=pc  →  ["pc"]     VR 화면에 PC 게임이 나온다
  //
  // 그 값들은 그 화면 필터 UI(`PLATFORM_FILTER_OPTIONS`)에 **떠 있고 고를 수 있다.**
  // 고른 대로 보여주는 쪽을 택했다 — 골랐는데 무시하면 R1 에서 고친 「죽은 컨트롤」로
  // 되돌아간다. 화면 제목과 목록이 어긋나 보이면 **필터 UI 에서 그 값을 빼는 것**이
  // 맞는 수정이지, 여기서 조용히 무시할 자리가 아니다.
  //
  // 플랫폼을 **전부 해제**하면 집합이 비어 그 화면의 기본값으로 돌아온다.
  const touchedPlatform = filters.some((f) => PLATFORM_VALUES.includes(f));
  if (!touchedPlatform) filters.push(...pathFacets);

  // ⚠️ **중복 제거를 두지 않는다.** 한때 넣었다가 뺐다 — 위 「기본값」 모델에서는
  // 유도분이 **쿼리에 플랫폼이 없을 때만** 얹히므로 겹칠 경로가 없다. 실제로 그
  // 방어를 지우는 뮤턴트가 **아무 테스트도 실패시키지 못했다**(전제가 거짓이라는 신호).
  // 남겨두면 다음 사람이 「누적을 막는 장치」로 읽고 위 모델을 안심하고 무너뜨린다.
  return filters.filter((f) => !!f);
};

// 웹 기본 정렬 — #5064.
//
// ⚠️ 한때 `popularity`(서버 라벨 "Recommended")로 바꿨다가(#5071) 되돌렸다.
// `popularity` 는 어드민 수동 입력 정수라 실사용과 어긋나 있다(백엔드 실측: 최근 28일
// 고유 uid 395명인 Tactical Assault VR 의 popularity 가 0). 백엔드가 자동 산출 필드
// `recommendedScore` 를 만들 예정이고, 이슈 #5064 의 작업 순서상 FE 디폴트 변경은
// 그 점수가 dev 에서 검증된 **뒤** 마지막에 하기로 정리됐다.
//
// 재전환 시 고칠 곳은 2곳뿐이다:
//   1) 아래 기본값 → `recommendedScore`
//   2) `src/constants/game-sort-options.ts` 의 폴백 옵션 (SDK options 응답과 1:1 유지)
// 딥링크에는 `sort=` 를 심지 않았으므로 기본값을 바꾸면 자동으로 따라온다.
const useSortBy = (query: ParsedUrlQuery): string => {
  const sortBy = query["sort"] || "monthlyPlayCount";
  return sortBy.toString();
};
const useOnlyGameKey = (query: ParsedUrlQuery) => {
  const gameKey = query["only-sellable"];
  const onlyGameKey = gameKey === "true";
  return onlyGameKey;
};

const DeviceTypePriority = [
  DeviceType.Vest,
  DeviceType.Arm,
  DeviceType.Head,
  DeviceType.Glove,
  DeviceType.Foot,
  DeviceType.Hand,
];

const DevicePriorityMap = new Map(
  DeviceTypePriority.map((type, index) => [type, index]),
);

function sortDevices(devices: DeviceType[]) {
  return devices.sort(
    (a, b) =>
      (DevicePriorityMap.get(a) ?? Infinity) -
      (DevicePriorityMap.get(b) ?? Infinity),
  );
}

/**
 * 신규 배지가 붙은 게임.
 *
 * ★**`platform` 은 값 하나도, 목록도 받는다** (#5583).
 *
 * `VR` 은 **단일 플랫폼 값이 아니다** — `VR_PLATFORM_VALUES` 6개
 * (`steam`·`quest`·`rift`·`vive`·`psvr2`·`pico`)의 묶음이고, `/games/vr/games` 목록도
 * 그 6개로 조회한다(`use-vr-games-filters.ts`). 값 하나만 받으면 VR 화면이 목록과
 * **다른 축**으로 조회하게 된다.
 *
 * ⚠️ `filters=vr` 라는 facet 도 서버에 실재하지만(2026-09-07 실측 87건) 6개 묶음과
 * **결과가 다르다**(같은 파라미터로 67건). 같은 페이지의 목록과 어긋나지 않으려면
 * 목록이 쓰는 축을 그대로 넘겨야 한다.
 *
 * 하위 호환이다 — 문자열 호출 10곳은 그대로 동작한다.
 */
export function useNewGames(platform?: string | readonly string[]) {
  const filters =
    platform === undefined
      ? []
      : typeof platform === "string"
        ? [platform]
        : [...platform];

  const { data, isLoading, isFetching } = useGames({
    filters,
    sortBy: "releaseTime",
  });

  const newGames = data?.filter((game) => game.badges.includes("New"));

  return { games: newGames ?? [], isLoading: isLoading || isFetching };
}

// AI Powered(AI 기반 자동 진동) 지원 게임 목록. 소비처는 ath/AiSupportedGames(AI Powered 랜딩) 1곳.
// `platform` 인자는 현재 미사용 확장점 — PC 게임 페이지가 `useAiPoweredGames("pc")` 로 쓰던
// 소비처였으나 PR #5079 로 그 섹션이 "지원 예정 게임"으로 교체됐다.
// 서버 facet 을 신뢰하고 클라 재필터는 하지 않는다 (바로 위 useNewGames 는 badges 로 재필터함 — 대비).
// ⚠️ 정렬 popularity 는 "많이 플레이된 순"이 아니라 어드민 큐레이션 순이다(서버 라벨 "Recommended").
// 실제 플레이 수 기준은 monthlyPlayCount. 노출 순서를 코드 수정 없이 어드민에서 제어하려고 택했다.
export function useAiPoweredGames(platform?: string) {
  const { data, isLoading, isFetching } = useGames({
    filters: platform ? [platform, AI_POWERED_FACET] : [AI_POWERED_FACET],
    sortBy: "popularity",
  });

  return { games: data ?? [], isLoading: isLoading || isFetching };
}

export function filterSellableGames(
  games: BhapticsContent[],
  countryCode: string,
): BhapticsContent[] {
  return games.filter(
    (game) => !game.gameResell?.disableCountryList?.includes(countryCode ?? ""),
  );
}

// 디바이스에 따른 판매 가능한 게임 목록을 반환하는 훅입니다.
// sku 를 이용해서 해당 디바이스의 gameResell 정보를 가져와서
// bhaptics contents 와 매칭합니다.
export function useBundleGames(items: { sku: string; quantity: number }[]) {
  const sellableGameResells = useSellableGames();
  const products = useProducts();

  const gameResells = useMemo(() => {
    const allSkuOptions =
      products.data?.flatMap((product) => product.skuOptions) ?? [];
    return items.flatMap((item) => {
      const option = allSkuOptions.find((opt) => opt.sku === item.sku);
      if (!option) return [];
      return Array.from(
        { length: item.quantity },
        () => option.gameResells,
      ).flat();
    });
  }, [products.data, items]);

  const bundlGames = useMemo(() => {
    return gameResells
      .map((resell) => {
        const game = sellableGameResells.data.find(
          (g) => g?.gameResell?.id === resell.id,
        );
        return game ? { ...game, bundle: resell } : null;
      })
      .filter((game) => game !== null);
  }, [gameResells, sellableGameResells.data]);

  return {
    games: bundlGames,
    isLoading: sellableGameResells.isLoading,
  };
}

export function useSellableGames(options?: {
  platform?: string;
  avaliableCountryOnly?: boolean;
  inStockOnly?: boolean;
  /**
   * 판매 목록 조회가 **실패한 동안** 주기적으로 다시 묻는다. 장바구니(`useCartWithProducts`)가 **게임이 담겨 있을 때만**
   * 켠다 — 목록을 모르면 장바구니의 게임 줄이 「재고 확인 중」으로 결제를 막는데(`@/libs/cart/cart-game-stock`),
   * 실패한 쿼리는 재시도(1회) 뒤 멈추고 창 포커스 재조회도 꺼져 있어(`_app.tsx`) 새로고침 전엔 풀리지 않는다. 목록
   * 화면은 종전대로 두려고 선택으로 둔다(폴링은 이 옵션을 준 관찰자에만 붙는다).
   */
  recheckOnError?: boolean;
}) {
  const platform = options?.platform ?? "";
  const avaliableCountryOnly = options?.avaliableCountryOnly ?? true;
  const inStockOnly = options?.inStockOnly ?? false;
  const recheckOnError = options?.recheckOnError ?? false;
  const currency = useCurrency();

  const { countryCode } = useLocale();
  const query = useQuery({
    ...GameQueryOptions.games({
      filters: platform,
      onlySellable: true,
    }),
    // 간격은 key-counts 의 「모름」 재조회와 같게 둔다 — 장바구니에서 둘 다 「재고 확인 중」을 푸는 신호다.
    refetchInterval: recheckOnError
      ? (q) =>
          q.state.status === "error" ? UNKNOWN_KEY_COUNTS_REFETCH_MS : false
      : undefined,
  });

  const games = avaliableCountryOnly
    ? filterSellableGames(query.data ?? [], countryCode)
    : (query.data ?? []);

  const gameResells = games
    .map((g) => g.gameResell)
    .filter((r): r is NonNullable<typeof r> => Boolean(r));
  const gameResellIds = gameResells.map((item) => item.id);
  const gameResellsKeyCounts = useGameResellsKeyCounts(
    inStockOnly ? gameResellIds : [],
  );

  // 재고가 있는 게임들만 필터링하는 경우
  // 해당 통화에 대한 가격이 있는 플랫폼만 남깁니다.
  const inStockGames = useMemo(() => {
    if (!inStockOnly) return [];

    // 재고를 확인하지 못한 게임(`data` null·undefined)은 숨긴다 — 확인 못 한 재고를 광고하지 않는다. 게임 상세 히어로 ·
    // 키 선택 다이얼로그와 같은 함수(`sellableKeyPlatforms`)다.
    // ⚠️ 장바구니는 이 결과를 쓰지 않는다. 장바구니는 「모름」을 숨기지 않고 「재고 확인 중」으로 남겨 결제만 막는
    // 판정(`@/libs/cart/cart-game-stock`)을 쓰고, 둘은 플랫폼 재고 술어(`platformKeyStock`) 하나를 공유한다.
    const resultsById = new Map(
      (gameResellsKeyCounts.data ?? []).map(
        (result) => [result.id, result.data] as const,
      ),
    );

    return games
      .map((game) => {
        if (!game.gameResell) return null;

        const availablePlatformList = sellableKeyPlatforms(
          game.gameResell.platformList,
          resultsById.get(game.gameResell.id),
          currency,
        );

        if (availablePlatformList.length === 0) {
          return null;
        }

        return {
          ...game,
          gameResell: {
            ...game.gameResell,
            platformList: availablePlatformList,
          },
        };
      })
      .filter((s) => !!s);
  }, [inStockOnly, games, gameResellsKeyCounts.data, currency]);

  const data = inStockOnly ? inStockGames : games;
  const _gameResells = useMemo(() => {
    return data.map((g) => g.gameResell).filter((r) => !!r);
  }, [data]);

  // 번들 노출 판정용 목록 — `_gameResells`(단독 판매 가능) + 번들 전용 게임 키(#6053).
  // 번들 전용 키는 가격 행이 없어(`isBundleOnlyGameResell` docblock) 위 `sellableKeyPlatforms`(가격 + 재고)에서 빠진다.
  // 번들에는 **재고만** 본다 — 같은 재고 술어(`platformKeyStock`)라 「모름」은 여기서도 숨긴다.
  // ⚠️ 단독 판매 표면(추천 레일 등)은 `gameResells` 를 써야 한다 — 이 목록을 쓰면 번들 전용 키를 단품으로 권한다.
  // ⚠️ `inStockOnly` 가 꺼져 있으면 번들 전용 키를 **넣지 않는다**(재고를 안 물으니 「재고 있음」을 확인할 수 없다).
  //    번들 판정 소비처(`useValidBundles` · `useGetBundleProducts`)는 둘 다 `inStockOnly: true` 로 부른다.
  const bundleGameResells = useMemo(() => {
    if (!inStockOnly) return _gameResells;
    const resultsById = new Map(
      (gameResellsKeyCounts.data ?? []).map(
        (result) => [result.id, result.data] as const,
      ),
    );
    const bundleOnly = games
      .map((g) => g.gameResell)
      .filter((r): r is NonNullable<typeof r> => isBundleOnlyGameResell(r))
      .map((r) => ({
        ...r,
        platformList: r.platformList.filter(
          (p) => platformKeyStock(resultsById.get(r.id), p.platform) === true,
        ),
      }))
      .filter((r) => r.platformList.length > 0);
    return [..._gameResells, ...bundleOnly];
  }, [inStockOnly, _gameResells, games, gameResellsKeyCounts.data]);

  return {
    data,
    gameResells: _gameResells,
    bundleGameResells,
    isLoading: query.isLoading || gameResellsKeyCounts.isLoading,
    isFetching: query.isFetching || gameResellsKeyCounts.isFetching,
    error: query.error,
  };
}

export function useBhapticsContents() {
  const router = useRouter();
  const { text, setText } = useStore();
  const sortBy = useSortBy(router.query);
  const { data: options } = useGameOptions();
  const filters = useFilters();
  const onlySellable = useOnlyGameKey(router.query);
  const getContents = useGames({ filters, sortBy, onlySellable });

  const filteredContents = useMemo(() => {
    return (
      getContents.data
        ?.filter((item) =>
          !text ? true : item.name.toLowerCase().includes(text.toLowerCase()),
        )
        ?.map((item) => {
          return produce(item, (draft) => {
            draft.compatibleDevices = sortDevices(draft.compatibleDevices);
            draft.supportedPlatforms.forEach((c, i) => {
              c.platform = ContentsService.friendlyPlatformName(c.platform);
            });
          });
        }) ?? []
    );
  }, [getContents.data, text]);

  const _options = useMemo(() => {
    if (options) {
      return {
        sortBy: options.sortBy,
        filters: options.filters,
        gameKeysOnly: onlySellable,
      };
    }
    return DEFAULT_GAME_OPTIONS;
  }, [options, onlySellable]);

  /**
   * ★**목적지를 현재 경로로 유지한다** (#5434).
   *
   * 종전엔 `/games/search` 를 하드코딩해서, 필터를 만지는 순간 **지금 보던 화면을
   * 벗어났다** — 탭에서는 서브내비와 그 화면 고유 섹션(지원 유형 안내 등)이 통째로
   * 사라졌다. 목록 자체는 쿼리로 옮겨가 맞았기에 **데이터가 아니라 탐색 결함**이다.
   *
   * `/games/search` 에서는 `router.pathname` 이 이미 그 값이라 **동작이 그대로**다.
   */
  const reset = () => {
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, filter: "" },
      },
      undefined,
    );
  };

  const handleSetFilters = (values: string[], gameKey?: boolean) => {
    handleQueries(values, sortBy, gameKey);
  };

  const toggle = (value: string) => {
    let newFilter = filters.includes(value)
      ? filters.filter((f) => f !== value)
      : [...filters, value];
    handleSetFilters(newFilter, onlySellable);
  };

  const toggleGameKey = () => {
    const gameKey = router.query["only-sellable"];
    const value = gameKey === "true" ? false : true;
    handleSetFilters(filters, value);
  };

  const handleSortBy = (sort: string) => {
    handleQueries(filters, sort, onlySellable);
  };

  const handleQueries = (
    filters: string[],
    sortBy: string,
    gameKey = onlySellable,
  ) => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          // 유도 facet 을 **빼지 않는다** — 빼면 사용자가 고른 플랫폼까지 사라진다
          // (#5434 R1 Major). 누적은 `useFilters` 의 `touchedPlatform` 게이트가 막는다
          // (한때 중복 제거로 막았는데 게이트가 생기면서 불필요해져 지웠다 — 그 함수
          // 주석 참조. 여기서 「중복 제거가 막는다」로 읽으면 게이트를 안심하고 건드린다).
          filter: filters.join(","),
          sort: sortBy,
          "only-sellable": gameKey,
        },
      },
      undefined,
    );
  };

  const selectedFilters = useMemo(() => {
    const allOptions = _options.filters.flatMap((f) =>
      f.options.map((o) => ({
        value: o.value,
        label: o.description,
        type: f.description as FilterType,
      })),
    );
    return allOptions.filter((o) => filters.includes(o.value));
  }, [_options.filters, filters]);

  return {
    search: text,
    selectedFilters,
    onlySellable,
    isLoading: getContents.isPending,
    resetFilters: () => reset(),
    setSearch: (s: string) => setText(s),
    toggleFilters: toggle,
    toggleGameKey,
    setFilters: handleSetFilters,
    setSortBy: handleSortBy,
    contents: filteredContents,
    rawContents: getContents.data,
    setQueries: handleQueries,
    sortBy,
    options: _options,
  };
}
